import React, { ReactElement, useState } from 'react';
import { observer } from 'mobx-react-lite';
import CustomerLoginForm, {
  CustomerLoginFormData,
} from '../../input/CustomerLoginForm';
import GuestInfoForm, { GuestInfoFormData } from '../../input/GuestInfoForm';
import ContactInfoSummary from '../../summary/ContactInfoSummary';
import { SectionSummaryGroupPosition } from '../../summary/SectionSummary';
import GuestLoginPrompt from '../../content/GuestLoginPrompt';
import RecognizedEmailLoginPrompt from '../../content/RecognizedEmailLoginPrompt';
import SectionTitle from '../../content/SectionTitle';
import ClosableModal from '../ClosableModal';
import { useMst } from '../../../models/stores/Root';

export const GuestContactSectionTestId = 'GuestContactSection-framework';

export type GuestContactSectionProps = {
  defaultEmailAddress?: string;
  displaySummary?: boolean;
  groupPosition?: SectionSummaryGroupPosition;
  onValidData: (formData: GuestInfoFormData) => void;
  onErrorStatus?: (hasError: boolean) => void;
  onContactInfoEdit?: () => void;
  isEmailRegistered?: boolean;
  tryCustomerLoginAsync?(
    formData: GuestInfoFormData
  ): Promise<string[] | undefined>;
  onLoginSuccess?(): void;
  onEmailCleared?(): void;
  loginRequired?: boolean;
  disableEmailAutoFocus?: boolean;
  forceEmailError?: boolean;
};

const GuestContactSection: React.FC<GuestContactSectionProps> = ({
  defaultEmailAddress,
  displaySummary = false,
  groupPosition,
  onValidData,
  onContactInfoEdit,
  onErrorStatus,
  isEmailRegistered,
  tryCustomerLoginAsync,
  onLoginSuccess,
  onEmailCleared,
  loginRequired = false,
  forceEmailError,
}: GuestContactSectionProps) => {
  const rootStore = useMst();
  const { configuration } = rootStore;
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [loginErrors, setLoginErrors] = useState<string[] | undefined>(
    undefined
  );

  const handleChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    if (event.target.value === '') {
      onEmailCleared?.();
    }
  };

  const openLoginModal = (): void => {
    setIsLoginModalOpen(true);
  };

  const closeLoginModal = (): void => {
    setLoginErrors(undefined);
    setIsLoginModalOpen(false);
  };

  const handleGuestInfoOnValidData = async (
    formData: GuestInfoFormData
  ): Promise<void> => {
    onValidData(formData);
  };

  const handleCustomerLoginOnValidData = async (
    formData: CustomerLoginFormData
  ): Promise<void> => {
    setLoginErrors(undefined);

    if (tryCustomerLoginAsync) {
      const errors = await tryCustomerLoginAsync(formData);
      if (errors) {
        setLoginErrors(errors);
      } else {
        setIsLoginModalOpen(false);
        onLoginSuccess?.();
      }
    }
  };

  const closableModalSubHeaderElm = (): ReactElement => {
    return <span className="m-2 text-md">Sign in for faster checkout.</span>;
  };

  const buildModalStyles = () => {
    if (!configuration?.reCaptcha?.loginEnabled) return {};

    let style = {};

    if (configuration?.reCaptcha?.size === 'invisible') {
      style = {
        paddingBottom: '80px',
      };
    }

    if (configuration?.reCaptcha?.size === 'normal') {
      style = {
        paddingLeft: '20px',
        paddingRight: '20px',
      };
    }

    return style;
  };

  return displaySummary ? (
    <ContactInfoSummary
      emailAddress={defaultEmailAddress || ''}
      onEditClicked={onContactInfoEdit}
      groupPosition={groupPosition}
    />
  ) : (
    <div data-testid={GuestContactSectionTestId}>
      <SectionTitle>Contact Information</SectionTitle>
      {tryCustomerLoginAsync && (
        <GuestLoginPrompt showLoginForm={openLoginModal} />
      )}
      <GuestInfoForm
        defaultEmailAddress={defaultEmailAddress}
        onValidData={handleGuestInfoOnValidData}
        onErrorStatus={onErrorStatus}
        onChange={handleChange}
        forceEmailError={forceEmailError}
      />
      {tryCustomerLoginAsync && (
        <>
          {isEmailRegistered && (
            <RecognizedEmailLoginPrompt
              showLoginForm={openLoginModal}
              loginRequired={loginRequired}
            />
          )}
          <ClosableModal
            isOpen={isLoginModalOpen}
            onRequestClose={closeLoginModal}
            label="Returning User"
            subHeader={closableModalSubHeaderElm()}
            style={buildModalStyles()}
          >
            <CustomerLoginForm
              autoFocus={!defaultEmailAddress}
              defaultEmailAddress={defaultEmailAddress}
              formErrors={loginErrors}
              onValidData={handleCustomerLoginOnValidData}
            />
          </ClosableModal>
        </>
      )}
    </div>
  );
};

export default observer(GuestContactSection);
